import { useEffect } from 'react';
import { useSettings } from '../context/SettingsContext.jsx';
import { uploadUrl } from '../api/client.js';
import { MORPH_BTN_SM } from '../lib/ui.js';

/**
 * Dialog "Install App" versi kustom, menggantikan tampilan polos bawaan
 * browser (native beforeinstallprompt dialog tidak bisa di-styling sama
 * sekali karena dirender di luar DOM/OS-level).
 *
 * Dua mode:
 * - 'native'  : browser support beforeinstallprompt -> tombol "Install" di
 *               sini akan memicu dialog konfirmasi native OS (langkah itu
 *               memang tidak bisa dilewati/di-skip oleh web app manapun),
 *               tapi pengalaman sebelum & sekitarnya jadi konsisten dengan
 *               desain situs.
 * - 'manual'  : browser TIDAK support beforeinstallprompt (mis. Android
 *               versi lama/Android 5.0 ke bawah, browser non-Chromium,
 *               atau iOS Safari) -> tampilkan panduan "Tambah ke Layar
 *               Utama" manual langkah demi langkah supaya pengguna tetap
 *               bisa install walau API-nya tidak tersedia.
 */
export default function InstallAppModal({ open, mode, onConfirm, onClose }) {
  const { settings } = useSettings();

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('modal-open');
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('modal-open');
    };
  }, [open, onClose]);

  if (!open) return null;

  const appName = settings.site_name || 'Exotic Pet World';
  const domain = typeof window !== 'undefined' ? window.location.hostname : '';
  const useLogo = settings.app_logo_mode === 'logo' && !!settings.app_logo;
  const isIOS = typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="install-modal-backdrop absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Install aplikasi"
        className="install-modal-panel relative w-full max-w-sm rounded-3xl border border-white/50 dark:border-neutral-700/60 bg-white/95 dark:bg-neutral-900/95 backdrop-blur-md shadow-[0_20px_60px_rgba(0,0,0,0.35)] p-6"
      >
        <div className="flex items-start gap-4 mb-6">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 overflow-hidden shadow-md"
            style={{ background: useLogo ? 'transparent' : `linear-gradient(180deg, var(--accent), var(--color-forest-700))` }}
          >
            {useLogo ? (
              <img src={uploadUrl(settings.app_logo)} alt={appName} className="w-full h-full object-cover" />
            ) : (
              <span className="material-symbols-outlined text-white text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>
                {settings.app_icon || 'pets'}
              </span>
            )}
          </div>
          <div className="min-w-0 pt-1">
            <h3 className="font-display font-extrabold text-lg text-neutral-900 dark:text-white leading-tight truncate">{appName}</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">{domain}</p>
          </div>
        </div>

        {mode === 'manual' ? (
          <>
            <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-4">
              Browser atau perangkat kamu belum mendukung instalasi otomatis. Tenang, tetap bisa dipasang manual kok:
            </p>
            <ol className="space-y-3 mb-6">
              {(isIOS
                ? [
                    <>Ketuk ikon <span className="material-symbols-outlined text-base align-middle">ios_share</span> (Share) di bar bawah Safari</>,
                    <>Gulir lalu pilih <strong className="text-neutral-800 dark:text-white">"Add to Home Screen"</strong></>,
                    <>Ketuk <strong className="text-neutral-800 dark:text-white">"Add"</strong> di pojok kanan atas buat konfirmasi</>,
                  ]
                : [
                    <>Ketuk ikon menu <span className="material-symbols-outlined text-base align-middle">more_vert</span> di pojok kanan atas browser</>,
                    <>Pilih <strong className="text-neutral-800 dark:text-white">"Tambahkan ke layar utama"</strong> / <strong className="text-neutral-800 dark:text-white">"Add to Home screen"</strong></>,
                    <>Ketuk <strong className="text-neutral-800 dark:text-white">"Tambahkan"</strong> / <strong className="text-neutral-800 dark:text-white">"Add"</strong> buat konfirmasi</>,
                  ]
              ).map((step, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-neutral-700 dark:text-neutral-200">
                  <span
                    className="flex-shrink-0 w-6 h-6 mt-0.5 rounded-full flex items-center justify-center text-[11px] font-extrabold text-white"
                    style={{ background: 'var(--accent)' }}
                  >
                    {i + 1}
                  </span>
                  <span className="leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className={`px-5 h-10 rounded-[20px] text-[13px] font-extrabold text-white shadow-md ${MORPH_BTN_SM}`}
                style={{ backgroundColor: 'var(--accent)' }}
              >
                Oke, Mengerti
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-6">
              Pasang aplikasi ini ke layar utama biar lebih cepat diakses dan tetap bisa dibuka walau koneksi lagi lemah.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={onClose}
                className={`px-5 h-10 rounded-[20px] text-[13px] font-extrabold text-neutral-600 dark:text-neutral-300 border border-neutral-200 dark:border-neutral-700 ${MORPH_BTN_SM}`}
              >
                Nanti Dulu
              </button>
              <button
                onClick={onConfirm}
                className={`px-5 h-10 rounded-[20px] text-[13px] font-extrabold text-white shadow-md ${MORPH_BTN_SM}`}
                style={{ backgroundColor: 'var(--accent)' }}
              >
                Install
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}